import { validateFile } from './file-validation';
import { deleteFileFromServer, uploadFileToServer } from './upload-api';
import { CSS_LIMIT_REACHED_CLASS, SELECTORS } from './upload-constants';
import { attachUploadEventListeners } from './upload-events';
import { createPreview, showError, updatePreviewCounts } from './upload-preview';
import { getOrCreateUploadSession } from './upload-session';
import { createUploadState } from './upload-state';

export default class UploadField {
	constructor({ htmlId, config, i18n, rest }) {
		this.config = config;
		this.htmlId = htmlId;
		this.i18n = i18n;
		this.rest = rest;
		this.uploadSession = getOrCreateUploadSession(config);

		this.zone = document.getElementById(`${htmlId}_dropzone`);
		this.fileInput = document.getElementById(htmlId);
		this.hiddenInput = document.getElementById(`${htmlId}_hidden`);

		if (!this.zone || !this.fileInput || !this.hiddenInput) {
			throw new Error(`Upload field "${htmlId}" is missing required DOM elements.`);
		}

		this.previewContainer = this.zone.querySelector(SELECTORS.previewContainer);
		this.errorContainer = this.zone.querySelector(SELECTORS.errorContainer);

		if (!this.previewContainer || !this.errorContainer) {
			throw new Error(`Upload field "${htmlId}" is missing preview or error containers.`);
		}

		this.uploadState = createUploadState({
			config: this.config,
			hiddenInput: this.hiddenInput,
		});
	}

	init() {
		this.updatePreviewListState();
		this.attachEventListeners();
	}

	getMaxFiles() {
		const maxFiles = Number(this.config.maxFiles);
		return Number.isFinite(maxFiles) && maxFiles > 0 ? maxFiles : 1;
	}

	getCurrentPreviewCount() {
		return this.previewContainer.querySelectorAll(SELECTORS.preview).length;
	}

	getRemainingSlots() {
		return Math.max(this.getMaxFiles() - this.getCurrentPreviewCount(), 0);
	}

	updatePreviewListState() {
		updatePreviewCounts(this.previewContainer, this.getMaxFiles());
		this.zone.classList.toggle(CSS_LIMIT_REACHED_CLASS, this.isAtLimit());
	}

	clearError() {
		this.errorContainer.innerHTML = '';
	}

	isAtLimit() {
		return this.getRemainingSlots() === 0;
	}

	openFilePicker() {
		if (typeof this.fileInput.showPicker === 'function') {
			this.fileInput.showPicker();
			return;
		}

		this.fileInput.click();
	}

	isPreviewInteractionTarget(target) {
		return target.closest(SELECTORS.removeButton) || target.closest(SELECTORS.preview);
	}

	storeUploadedFileMetadata(fileId, file, uploadData) {
		this.uploadState.set(fileId, file, uploadData);
	}

	removeUploadedFile(fileId, preview) {
		this.uploadState.delete(fileId);
		preview.remove();
		this.updatePreviewListState();
	}

	async deleteStoredFile(fileMetadata) {
		try {
			await deleteFileFromServer(this.config, this.rest, fileMetadata, this.uploadSession);
			return true;
		} catch (error) {
			console.error('Error deleting file from server:', error);
			showError(this.errorContainer, this.i18n.deleteFailed);
			return false;
		}
	}

	resetUploads() {
		const files = this.uploadState.clear();
		this.previewContainer.replaceChildren();
		this.fileInput.value = '';
		this.clearError();
		this.updatePreviewListState();
		files.forEach((file) => this.deleteStoredFile(file));
	}

	attachRemoveButtonListener(fileId, preview) {
		preview.addEventListener('click', async (event) => {
			const target = event.target;
			if (target instanceof HTMLElement && target.closest(SELECTORS.removeButton)) {
				event.preventDefault();

				const fileMetadata = this.uploadState.get(fileId);

				if (fileMetadata?.storageKey) {
					const confirmed = window.confirm(this.i18n.confirmDelete);
					if (!confirmed) {
						return;
					}

					if (await this.deleteStoredFile(fileMetadata)) {
						this.removeUploadedFile(fileId, preview);
					}
				} else {
					this.removeUploadedFile(fileId, preview);
				}
			}
		});
	}

	attachEventListeners() {
		// Reset the upload field when Formidable clears the hidden field.
		window.jQuery?.(this.hiddenInput).on('change', () => {
			if (this.hiddenInput.value === '') {
				this.resetUploads();
			}
		});

		attachUploadEventListeners({
			zone: this.zone,
			fileInput: this.fileInput,
			isAtLimit: this.isAtLimit.bind(this),
			isPreviewInteractionTarget: this.isPreviewInteractionTarget.bind(this),
			openFilePicker: this.openFilePicker.bind(this),
			handleFileSelection: this.handleFileSelection.bind(this),
		});
	}

	handleFileSelection(files) {
		if (!files?.length) {
			return;
		}

		this.clearError();

		if (this.isAtLimit()) {
			this.updatePreviewListState();
			return;
		}

		Array.from(files)
			.slice(0, this.getRemainingSlots())
			.forEach((file) => {
				this.uploadFile(file);
			});
	}

	createUploadPreview(file, fileId) {
		const nextIndex = this.getCurrentPreviewCount() + 1;
		const preview = createPreview(file, fileId, {
			removeFileLabel: this.i18n.removeFile,
			countLabel: `${nextIndex}/${this.getMaxFiles()}`,
		});

		this.previewContainer.appendChild(preview);
		this.updatePreviewListState();
		this.attachRemoveButtonListener(fileId, preview);

		return preview;
	}

	async uploadFile(file) {
		const validation = validateFile(file, this.config, this.i18n);
		if (validation.error) {
			showError(this.errorContainer, validation.message);
			return;
		}

		const fileId = `file_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
		const preview = this.createUploadPreview(file, fileId);

		const updateProgress = (percent) => {
			const progressElement = preview.querySelector(SELECTORS.previewProgress);
			const progressText = preview.querySelector(SELECTORS.previewProgressText);

			if (progressElement) {
				progressElement.style.setProperty('--progress', `${percent}%`);
			}
			if (progressText) {
				progressText.textContent = `${percent}%`;
			}
		};

		try {
			const uploadData = await uploadFileToServer(file, this.config, this.rest, this.uploadSession, updateProgress);
			if (!uploadData?.storage_key) {
				throw new Error('Missing server storage key');
			}
			// Don't let an upload that finishes later fill a field that's already been cleared
			if (!preview.isConnected) {
				await this.deleteStoredFile({ storageKey: uploadData.storage_key });
				return;
			}

			preview.classList.add('frm_upload_success');
			this.storeUploadedFileMetadata(fileId, file, uploadData);
		} catch {
			if (!preview.isConnected) {
				return;
			}
			preview.remove();
			this.updatePreviewListState();
			showError(this.errorContainer, this.i18n.uploadFailed);
		}
	}
}
